import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { Search, Download, Printer, Eye, FileText, Calendar, Plus } from 'lucide-react';
import { receiptService } from '../lib/services.accounts';
import { printReceipt, downloadReceipt } from '../lib/receiptPdf';
import type { Receipt } from '../lib/supabase';
import toast from 'react-hot-toast';

const ReceiptManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [filteredReceipts, setFilteredReceipts] = useState<Receipt[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    receiptType: ''
  });

  useEffect(() => {
    loadReceipts();
  }, []);

  useEffect(() => {
    filterReceipts();
  }, [searchTerm, receipts, filters]);

  const loadReceipts = async () => {
    try {
      setLoading(true);
      const data = await receiptService.getAll(filters);
      setReceipts(data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load receipts');
    } finally {
      setLoading(false);
    }
  };

  const filterReceipts = () => {
    let filtered = [...receipts];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(receipt =>
        receipt.receipt_number.toLowerCase().includes(term) ||
        receipt.patient?.name.toLowerCase().includes(term) ||
        receipt.patient?.unique_id?.toLowerCase().includes(term) ||
        receipt.invoice?.invoice_number.toLowerCase().includes(term)
      );
    }

    setFilteredReceipts(filtered);
  };

  const applyFilters = () => {
    loadReceipts();
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      receiptType: ''
    });
    setSearchTerm('');
  };

  const handlePrint = async (receipt: Receipt) => {
    try {
      await printReceipt(receipt, receipt.receipt_type);
      
      await receiptService.markAsPrinted(receipt.id);
      
      loadReceipts();
      
      toast.success('Receipt sent to printer');
    } catch (error: any) {
      toast.error(error.message || 'Failed to print receipt');
    }
  };

  const handleDownload = (receipt: Receipt) => {
    try {
      downloadReceipt(receipt, receipt.receipt_type);
      toast.success('Receipt downloaded successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to download receipt');
    }
  };

  const handlePreview = (receipt: Receipt) => {
    setSelectedReceipt(receipt);
    setShowPreview(true);
  };

  const getReceiptTypeColor = (type: string) => {
    const colors: { [key: string]: string } = {
      standard: 'bg-primary-100 text-primary-800',
      detailed: 'bg-secondary-100 text-secondary-800',
      summary: 'bg-accent-100 text-accent-800'
    };
    return colors[type] || 'bg-neutral-100 text-neutral-800';
  };

  const totalAmount = filteredReceipts.reduce((sum, receipt) => sum + Number(receipt.amount), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-neutral-600">Loading receipts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900">Receipt Management</h1>
          <p className="text-neutral-600 mt-1">View, print, and manage payment receipts</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-600 text-sm font-medium">Total Receipts</p>
              <p className="text-2xl font-bold text-neutral-900 mt-2">{filteredReceipts.length}</p>
            </div>
            <div className="bg-primary-100 rounded-full p-3">
              <FileText className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-600 text-sm font-medium">Total Amount</p>
              <p className="text-2xl font-bold text-neutral-900 mt-2">${totalAmount.toFixed(2)}</p>
            </div>
            <div className="bg-success-100 rounded-full p-3">
              <FileText className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-600 text-sm font-medium">Standard</p>
              <p className="text-2xl font-bold text-neutral-900 mt-2">
                {filteredReceipts.filter(r => r.receipt_type === 'standard').length}
              </p>
            </div>
            <div className="bg-primary-100 rounded-full p-3">
              <FileText className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-600 text-sm font-medium">Detailed</p>
              <p className="text-2xl font-bold text-neutral-900 mt-2">
                {filteredReceipts.filter(r => r.receipt_type === 'detailed').length}
              </p>
            </div>
            <div className="bg-secondary-100 rounded-full p-3">
              <FileText className="w-6 h-6 text-secondary-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Receipt Type</label>
            <select
              value={filters.receiptType}
              onChange={(e) => setFilters({ ...filters, receiptType: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Types</option>
              <option value="standard">Standard</option>
              <option value="detailed">Detailed</option>
              <option value="summary">Summary</option>
            </select>
          </div>
          
          <div className="flex items-end gap-2">
            <button
              onClick={applyFilters}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Apply
            </button>
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-neutral-200 text-neutral-700 rounded-lg hover:bg-neutral-300 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by receipt number, patient name, patient ID, or invoice number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Receipt Types Info */}
      <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-3">Receipt Types</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-primary-100 rounded p-2">
                <FileText className="w-4 h-4 text-primary-600" />
              </div>
              <h4 className="font-semibold text-neutral-900">Standard</h4>
            </div>
            <p className="text-sm text-neutral-600">Simple and clean receipt with essential payment information</p>
          </div>
          
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-secondary-100 rounded p-2">
                <FileText className="w-4 h-4 text-secondary-600" />
              </div>
              <h4 className="font-semibold text-neutral-900">Detailed</h4>
            </div>
            <p className="text-sm text-neutral-600">Comprehensive receipt with invoice breakdown and financial details</p>
          </div>
          
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-accent-100 rounded p-2">
                <FileText className="w-4 h-4 text-accent-600" />
              </div>
              <h4 className="font-semibold text-neutral-900">Summary</h4>
            </div>
            <p className="text-sm text-neutral-600">Compact and minimal receipt for quick reference</p>
          </div>
        </div>
      </div>

      {/* Receipts Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Receipt Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Patient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {filteredReceipts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-neutral-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
                    <p className="text-lg font-medium">No receipts found</p>
                    <p className="text-sm mt-1">Try adjusting your filters or search criteria</p>
                  </td>
                </tr>
              ) : (
                filteredReceipts.map((receipt) => (
                  <tr key={receipt.id} className="hover:bg-neutral-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-neutral-900">{receipt.receipt_number}</div>
                      {receipt.invoice && (
                        <div className="text-xs text-neutral-500">Invoice: {receipt.invoice.invoice_number}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                      {new Date(receipt.generated_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {receipt.patient && (
                        <>
                          <div className="text-sm font-medium text-neutral-900">{receipt.patient.name}</div>
                          {receipt.patient.unique_id && (
                            <div className="text-sm text-neutral-500">{receipt.patient.unique_id}</div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getReceiptTypeColor(receipt.receipt_type)}`}>
                        {receipt.receipt_type.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900 text-right">
                      ${Number(receipt.amount).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {receipt.printed_at ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-800">
                          PRINTED
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-neutral-100 text-neutral-800">
                          NOT PRINTED
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handlePreview(receipt)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Preview"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handlePrint(receipt)}
                          className="text-secondary-600 hover:text-secondary-900"
                          title="Print"
                        >
                          <Printer className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDownload(receipt)}
                          className="text-accent-600 hover:text-accent-900"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && selectedReceipt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-neutral-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-neutral-900">Receipt Preview</h2>
                <button
                  onClick={() => setShowPreview(false)}
                  className="text-neutral-400 hover:text-neutral-600"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="bg-neutral-50 rounded-lg p-6 space-y-4">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-neutral-900">PAYMENT RECEIPT</h3>
                  <p className="text-sm text-neutral-600 mt-1">MedSystem Hospital Management</p>
                </div>
                
                <div className="border-t border-neutral-200 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-neutral-600">Receipt Number</p>
                      <p className="font-semibold text-neutral-900">{selectedReceipt.receipt_number}</p>
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600">Date</p>
                      <p className="font-semibold text-neutral-900">
                        {new Date(selectedReceipt.generated_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
                
                {selectedReceipt.patient && (
                  <div className="border-t border-neutral-200 pt-4">
                    <p className="text-sm text-neutral-600 mb-2">Patient Information</p>
                    <p className="font-semibold text-neutral-900">{selectedReceipt.patient.name}</p>
                    {selectedReceipt.patient.unique_id && (
                      <p className="text-sm text-neutral-600">ID: {selectedReceipt.patient.unique_id}</p>
                    )}
                  </div>
                )}
                
                {selectedReceipt.invoice && (
                  <div className="border-t border-neutral-200 pt-4">
                    <p className="text-sm text-neutral-600 mb-2">Invoice Details</p>
                    <p className="font-semibold text-neutral-900">{selectedReceipt.invoice.invoice_number}</p>
                  </div>
                )}
                
                <div className="border-t border-neutral-200 pt-4">
                  <div className="bg-primary-600 text-white rounded-lg p-4 text-center">
                    <p className="text-sm">Amount Paid</p>
                    <p className="text-3xl font-bold">${Number(selectedReceipt.amount).toFixed(2)}</p>
                    {selectedReceipt.payment_method && (
                      <p className="text-sm mt-1">via {selectedReceipt.payment_method.toUpperCase()}</p>
                    )}
                  </div>
                </div>
                
                <div className="text-center text-sm text-neutral-500 italic">
                  <p>Thank you for your payment!</p>
                  <p className="mt-1">This is a computer-generated receipt</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-neutral-200 flex gap-3">
              <button
                onClick={() => handlePrint(selectedReceipt)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-secondary-600 text-white rounded-lg hover:bg-secondary-700 transition-colors"
              >
                <Printer className="w-4 h-4" />
                Print
              </button>
              <button
                onClick={() => handleDownload(selectedReceipt)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 bg-neutral-200 text-neutral-700 rounded-lg hover:bg-neutral-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceiptManagement;
